import { UnderlinedTokens } from "@/components/shared/UnderlinedTokens";
import {
  ExtractWordAndGrammarFromImageDocument,
  GetSlideInheritedLimitDocument,
} from "@/generated/graphql";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import { type GrammarAuthoringIssue, validateGrammarFormAuthoring } from "@ai-teach/utils";
import { useMutation, useQuery } from "@apollo/client/react";
import { useRef } from "react";
import toast from "react-hot-toast";
import { AiOutlineAppstoreAdd } from "react-icons/ai";
import { BiText } from "react-icons/bi";
import { HiExclamationCircle, HiOutlineUpload } from "react-icons/hi";
import { GrammarFocusUnderlineEditor } from "./GrammarFocusUnderlineEditor";

const IMAGE_EXTRACT_TOAST_ID = "extractWordAndGrammarFromImage";
// Matches the backend's MAX_IMAGE_UPLOAD_SIZE in slide-handlers.resolvers.ts.
const MAX_IMAGE_UPLOAD_SIZE = 10 * 1024 * 1024;

/** Uploads a photo, reads any words/grammar it contains, and drops the result into the Word/Grammar
 *  focus boxes for the teacher to review — the image itself is never stored. */
export function ImageExtractButton() {
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [extract, { loading }] = useMutation(ExtractWordAndGrammarFromImageDocument);

  const handleFile = async (file: File) => {
    if (!slide) return;
    if (file.size > MAX_IMAGE_UPLOAD_SIZE) {
      toast.error(`File is too large. Maximum size is ${MAX_IMAGE_UPLOAD_SIZE / 1024 / 1024}MB.`, {
        id: IMAGE_EXTRACT_TOAST_ID,
      });
      return;
    }
    toast.loading("Reading image…", { id: IMAGE_EXTRACT_TOAST_ID });

    let result: { words: string; grammar: string } | null;
    try {
      const res = await extract({ variables: { image: file } });
      result = res.data?.extractWordAndGrammarFromImage ?? null;
    } catch (error) {
      if (!toastAiCredits(error, IMAGE_EXTRACT_TOAST_ID)) {
        toast.error("Couldn't read that image. Please try again.", { id: IMAGE_EXTRACT_TOAST_ID });
      }
      return;
    }

    if (!result || (!result.words && !result.grammar)) {
      toast.error("No vocabulary or grammar found in that image. Try a different page.", {
        id: IMAGE_EXTRACT_TOAST_ID,
      });
      return;
    }

    dispatch(
      updateSlide({
        ...slide,
        slideId: slide.id,
        limitation: {
          ...slide.limitation,
          ...(result.words && { wordFocus: result.words }),
          ...(result.grammar && { grammarFocus: result.grammar }),
        },
      })
    );
    toast.success("Done — review the extracted text below.", { id: IMAGE_EXTRACT_TOAST_ID });
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) void handleFile(file);
        }}
      />
      <button
        type="button"
        disabled={loading}
        onClick={() => inputRef.current?.click()}
        className="shrink-0 flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200 rounded-lg border border-indigo-200 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        <HiOutlineUpload className="w-4 h-4" />
        {loading ? "Reading…" : "Upload image"}
      </button>
    </>
  );
}

interface WordGrammarLimitFormProps {
  type: "word" | "grammar";
}

/**
 * The teacher authors only the slide's FOCUS — the words/grammar this slide teaches. The "limit"
 * (what the student may use) is DERIVED automatically by accumulating every earlier slide's Focus,
 * shown read-only here so the author can see the growing known-set. There is no per-slide limit to
 * author.
 */
export function WordGrammarLimitForm({ type }: WordGrammarLimitFormProps) {
  const isWord = type === "word";
  const title = isWord ? "Word" : "Grammar";
  const lowercaseTitle = title.toLowerCase();

  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const dispatch = useAppDispatch();

  const focusKey = isWord ? "wordFocus" : "grammarFocus";
  const slideFocus = slide?.limitation?.[focusKey];

  // The accumulated known-set ("limit") inherited from earlier form slides/lessons/projects —
  // computed server-side from prior slides' Focus. Read-only.
  const isFormSlide = slide?.template === "conversation";
  const { data: inheritedData } = useQuery(GetSlideInheritedLimitDocument, {
    variables: { id: slide?.id ?? "" },
    skip: !slide?.id || !isFormSlide,
    fetchPolicy: "cache-and-network",
  });
  const inherited =
    (isWord
      ? inheritedData?.getSlide?.inheritedLimit?.wordLimit
      : inheritedData?.getSlide?.inheritedLimit?.grammarLimit) ?? [];

  const updateLimitation = (partial: Record<string, unknown>) => {
    if (!slide) return;
    dispatch(
      updateSlide({
        ...slide,
        slideId: slide.id,
        limitation: {
          ...slide.limitation,
          ...partial,
        },
      })
    );
  };

  const handleFocusChange = (text: string) => updateLimitation({ [focusKey]: text });

  // Shared authoring validator — the SAME source of truth getSlideWarning uses for the sidebar
  // publication checklist, so the inline editor and the checklist always show identical messages and
  // line numbers ("Line N: message").
  const wordFocusValue = slide?.limitation?.wordFocus;
  const validation = isWord
    ? null
    : validateGrammarFormAuthoring({ grammarFocus: slideFocus, wordFocus: wordFocusValue });

  const formatIssue = (issue: GrammarAuthoringIssue): string =>
    issue.line !== null ? `Line ${issue.line}: ${issue.message}` : issue.message;

  // Missing-field issues aren't relevant here — this form only edits the focus text itself.
  const relevantIssues = (validation?.issues ?? []).filter(
    (issue) => issue.code !== "grammar_focus_required" && issue.code !== "word_focus_required"
  );

  // HARD errors: invalid authored content that would break the runtime or block publish (round 8+).
  const focusErrors = relevantIssues.filter((issue) => issue.severity === "error").map(formatIssue);
  // Content-quality flags on the authored dialogue (advisory) — do not block publish.
  const focusIssues = relevantIssues
    .filter((issue) => issue.severity === "warning")
    .map(formatIssue);

  // A staged AI rewrite of grammarFocus (from the normalize-grammar-focus migration) awaiting review.
  const normalizedDraft = isWord ? null : (slide?.limitation?.grammarFocusNormalized ?? null);
  const needsReview = !isWord && slide?.limitation?.grammarFocusNeedsReview === true;

  // Approve: replace grammarFocus with the draft and clear the staging fields.
  const applyNormalized = () => {
    if (!normalizedDraft) return;
    updateLimitation({
      grammarFocus: normalizedDraft,
      grammarFocusNormalized: null,
      grammarFocusNeedsReview: null,
    });
  };
  // Reject: drop the draft, keep the current grammarFocus.
  const dismissNormalized = () =>
    updateLimitation({ grammarFocusNormalized: null, grammarFocusNeedsReview: null });
  // Let the author hand-correct the AI draft before applying — edits persist to the staging field,
  // so Apply still copies the (now-edited) draft into grammarFocus.
  const handleDraftChange = (text: string) => updateLimitation({ grammarFocusNormalized: text });

  return (
    <div className="flex flex-col gap-3 mx-2 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <BiText className="w-4 h-4 text-primary-500 shrink-0" />
          <h1 className="text-sm font-semibold text-gray-800 truncate">
            {title}s this slide teaches
          </h1>
        </div>
        <button
          type="button"
          onClick={() => handleFocusChange("")}
          className="shrink-0 text-xs font-medium text-gray-400 hover:text-red-600 transition-colors"
        >
          Clear
        </button>
      </div>

      <p className="text-xs text-gray-500 -mt-1">
        Only the {lowercaseTitle}s introduced here. Earlier slides' {lowercaseTitle}s carry over
        automatically (shown below).
      </p>

      <div className="flex flex-col gap-2">
        {isWord ? (
          <textarea
            name={`${lowercaseTitle}-focus`}
            id={`${lowercaseTitle}-focus`}
            value={slideFocus ?? ""}
            onChange={(e) => handleFocusChange(e.target.value)}
            placeholder={`One ${lowercaseTitle} per line…`}
            className="w-full h-40 border text-black border-gray-300 rounded-lg resize-none p-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        ) : (
          // Grammar focus is an example conversation; the Underline tool marks the swappable words.
          <GrammarFocusUnderlineEditor
            value={slideFocus ?? ""}
            onChange={handleFocusChange}
            placeholder="Type the example conversation this slide teaches..."
            resetKey={slide?.id}
          />
        )}

        {/* HARD errors: unusable characters that block the slide from students (round 8). */}
        {focusErrors.length > 0 && (
          <div className="flex flex-col gap-1 p-2 bg-error-50 rounded-lg border-2 border-error-500">
            <div className="flex items-center gap-1.5">
              <HiExclamationCircle className="w-4 h-4 text-error-600 flex-shrink-0" />
              <p className="text-xs font-semibold text-error-800">
                Invalid characters — fix to publish
              </p>
            </div>
            <ul className="list-disc pl-5 space-y-0.5">
              {focusErrors.map((error) => (
                <li key={error} className="text-[11px] text-error-700">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Content-quality flags on the authored dialogue (advisory). */}
        {focusIssues.length > 0 && (
          <div className="flex flex-col gap-1 p-2 bg-amber-50 rounded-lg border border-amber-300">
            <div className="flex items-center gap-1.5">
              <HiExclamationCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <p className="text-xs font-semibold text-amber-800">Check this dialogue</p>
            </div>
            <ul className="list-disc pl-5 space-y-0.5">
              {focusIssues.map((issue) => (
                <li key={issue} className="text-[11px] text-amber-700">
                  {issue}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Staged AI rewrite awaiting review (from the legacy normalization migration). */}
        {normalizedDraft && (
          <div className="flex flex-col gap-2 p-2 bg-amber-50 rounded-lg border border-amber-300">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-amber-800">Suggested rewrite (AI)</p>
              {needsReview && (
                <span className="text-[10px] px-2 py-0.5 bg-amber-200 text-amber-900 rounded font-medium">
                  Needs review
                </span>
              )}
            </div>
            <p className="text-[11px] text-amber-700">
              Underlined words are the ones that can change. Edit the text or underlines as needed,
              then Apply to use this version — or dismiss to keep the current one.
            </p>
            <GrammarFocusUnderlineEditor
              value={normalizedDraft}
              onChange={handleDraftChange}
              resetKey={`${slide?.id}-draft`}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={applyNormalized}
                className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={dismissNormalized}
                className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Auto-derived known-set (read-only) — the accumulation of earlier slides' Focus. */}
        {isFormSlide && (
          <div className="flex flex-col gap-1 p-2 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-1.5">
              <AiOutlineAppstoreAdd className="w-4 h-4 text-green-700" />
              <p className="text-xs font-semibold text-green-800">
                Already known — auto-accumulated from earlier slides
              </p>
            </div>
            {inherited.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {inherited.map((item) => (
                  <span
                    key={item}
                    className="text-xs px-2 py-0.5 bg-green-50 text-green-800 border border-green-200 rounded"
                  >
                    <UnderlinedTokens text={item} />
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs italic text-green-700">
                Nothing yet — this is the first form slide.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
